import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Menu,
  X,
  User,
  LogOut,
  Settings,
  Calendar,
  Shield,
  Search,
  ListOrdered,
  Info,
} from 'lucide-react';
import NotificationBell from './NotificationBell';
import LogoutModal from './LogoutModal';

const ClientHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const userMenuRef = useRef(null);
  const sheetRef = useRef(null);

  // Transparent header only over the homepage full-bleed hero.
  // Other marketing pages keep a solid black header.
  const isDarkHeroPage = location.pathname === '/';

  const openLogoutModal = () => {
    setIsUserMenuOpen(false);
    setIsMenuOpen(false);
    setShowLogoutModal(true);
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
      navigate('/');
    } finally {
      setLoggingOut(false);
      setShowLogoutModal(false);
    }
  };

  const closeMenu = () => setIsMenuOpen(false);

  // Handle scroll detection for transparent/black header
  useEffect(() => {
    let rafId = null;
    let ticking = false;

    const checkScroll = () => {
      setIsScrolled(window.scrollY > 50);
      ticking = false;
    };

    checkScroll();

    const handleScroll = () => {
      if (!ticking) {
        ticking = true;
        rafId = requestAnimationFrame(checkScroll);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Lock body scroll + Escape to close when mobile menu is open
  useEffect(() => {
    if (!isMenuOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeMenu();
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isMenuOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    closeMenu();
  }, [location.pathname]);

  const navigation = [
    { name: 'Browse', href: '/browse-vendors', icon: Search },
    { name: 'How it works', href: '/how-it-works', icon: ListOrdered },
    { name: 'About', href: '/about', icon: Info },
  ];

  const shouldBeTransparent = isDarkHeroPage && !isScrolled;

  const menuItemClass =
    'flex w-full items-center px-4 py-2.5 text-sm text-m-ink-muted transition-colors hover:bg-m-canvas-light hover:text-m-ink';

  const isAdmin = user?.user_type === 'admin';
  const isVendor = user?.user_type === 'vendor';

  const profileHref = isAdmin
    ? '/admin'
    : isVendor
      ? '/vendor'
      : '/bookings';

  const dashboardHref = isAdmin ? '/admin' : isVendor ? '/vendor' : '/dashboard';
  const dashboardLabel = isAdmin ? 'Go to Admin' : isVendor ? 'Vendor Dashboard' : 'My Bookings';
  const DashboardIcon = isAdmin ? Shield : Calendar;

  const settingsHref = isAdmin
    ? '/admin/settings'
    : isVendor
      ? '/vendor/profile'
      : '/settings';

  const mobileMenu =
    isMenuOpen &&
    createPortal(
      <div
        className="theme-marketing marketing-app fixed inset-0 z-[11000] flex flex-col justify-end md:hidden"
        role="dialog"
        aria-modal="true"
      >
        <button
          type="button"
          aria-label="Close menu"
          className="absolute inset-0 animate-fade-in bg-black/55 backdrop-blur-[2px]"
          onClick={closeMenu}
        />

        <div
          ref={sheetRef}
          className="relative flex max-h-[88vh] w-full animate-slideUp flex-col overflow-hidden rounded-t-3xl bg-m-night text-m-on-dark shadow-sm"
          style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        >
          <div className="flex shrink-0 items-center justify-between px-5 pb-2 pt-3">
            <div className="w-10" />
            <div className="h-1 w-10 rounded-full bg-white/30" aria-hidden="true" />
            <button
              type="button"
              onClick={closeMenu}
              className="flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-white/10"
              aria-label="Close menu"
            >
              <X className="h-5 w-5 text-white" />
            </button>
          </div>

          <div className="overscroll-contain overflow-y-auto px-5 pb-2">
            <div className="mb-5 flex items-center gap-3">
              <img src="/images/VendaLOGO1.png" alt="VendaList" className="h-8 w-auto" />
            </div>

            <nav className="mb-5 space-y-1.5">
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={closeMenu}
                    className={`flex items-center gap-3 rounded-xl px-4 py-3.5 text-[15px] font-medium transition-colors ${
                      isActive
                        ? 'bg-white text-m-ink'
                        : 'text-white/90 hover:bg-white/10 active:bg-white/15'
                    }`}
                  >
                    <Icon
                      className={`h-5 w-5 shrink-0 ${
                        isActive ? 'text-m-ink' : 'text-white/70'
                      }`}
                    />
                    {item.name}
                  </Link>
                );
              })}
            </nav>

            <div className="mb-5 h-px bg-white/15" />

            {isAuthenticated ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 rounded-xl bg-white/5 p-1.5">
                  <Link
                    to={profileHref}
                    onClick={closeMenu}
                    className="flex min-w-0 flex-1 items-center gap-3 rounded-xl px-2 py-2 transition-colors hover:bg-white/10 active:bg-white/15"
                    aria-label={
                      isAdmin || isVendor ? 'Open dashboard' : 'Open bookings'
                    }
                  >
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/15">
                      <User className="h-5 w-5 text-white" />
                    </div>
                    <div className="min-w-0 flex-1 text-left">
                      <p className="truncate text-sm font-medium text-white">
                        {user?.name || 'User'}
                      </p>
                      <p className="mt-0.5 truncate text-xs text-white/55">
                        {user?.email}
                      </p>
                    </div>
                  </Link>
                  <div className="shrink-0 pr-1" onClick={(e) => e.stopPropagation()}>
                    <NotificationBell buttonClassName="text-white/70 hover:bg-white/10 hover:text-white" />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Link
                    to={dashboardHref}
                    onClick={closeMenu}
                    className="flex items-center gap-3 rounded-xl px-4 py-3.5 text-[15px] font-medium text-white/90 transition-colors hover:bg-white/10"
                  >
                    <DashboardIcon className="h-5 w-5 text-white/70" />
                    {dashboardLabel}
                  </Link>
                  <Link
                    to={settingsHref}
                    onClick={closeMenu}
                    className="flex items-center gap-3 rounded-xl px-4 py-3.5 text-[15px] font-medium text-white/90 transition-colors hover:bg-white/10"
                  >
                    <Settings className="h-5 w-5 text-white/70" />
                    Profile Settings
                  </Link>
                  <button
                    type="button"
                    onClick={openLogoutModal}
                    className="flex w-full items-center gap-3 rounded-xl px-4 py-3.5 text-[15px] font-medium text-red-300 transition-colors hover:bg-red-500/10"
                  >
                    <LogOut className="h-5 w-5" />
                    Sign Out
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3 pb-1">
                <Link
                  to="/login"
                  onClick={closeMenu}
                  className="block w-full rounded-full border border-white/25 py-3.5 text-center text-[15px] font-medium text-white transition-colors hover:bg-white/10"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  onClick={closeMenu}
                  className="block w-full rounded-full bg-white py-3.5 text-center text-[15px] font-medium text-m-ink transition-colors hover:bg-white/90"
                >
                  Get Started
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>,
      document.body
    );

  return (
    <header
      className={`sticky top-0 z-[10000] transition-all duration-300 ${
        shouldBeTransparent
          ? ''
          : 'border-b border-black bg-black shadow-sm'
      }`}
      style={
        shouldBeTransparent
          ? { backgroundColor: 'transparent', backdropFilter: 'none', borderBottom: 'none' }
          : undefined
      }
    >
      <div className="page-container">
        <div className="flex h-[4.25rem] items-center justify-between">
          <Link to="/" className="flex items-center space-x-2">
            <img src="/images/VendaLOGO1.png" alt="Logo" className="h-11 w-auto" />
          </Link>

          {/* Desktop Navigation — Inter 400 nav (Shopify UI weight) */}
          <nav className="hidden items-center gap-0.5 md:flex">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`px-3.5 py-2 text-[15px] font-normal tracking-tight transition-colors ${
                  shouldBeTransparent
                    ? 'text-white/85 hover:text-white'
                    : 'text-white/70 hover:text-white'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Desktop Auth Section */}
          <div className="hidden items-center gap-3 md:flex">
            {isAuthenticated && (
              <NotificationBell
                className="mr-1"
                buttonClassName="text-white/70 hover:bg-white/10 hover:text-white"
              />
            )}
            {isAuthenticated ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className={`flex items-center space-x-2 transition-colors ${
                    shouldBeTransparent
                      ? 'text-white drop-shadow-md hover:text-white'
                      : 'text-white/70 hover:text-white'
                  }`}
                >
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-full ${
                      shouldBeTransparent ? 'bg-white/20' : 'bg-white/15'
                    }`}
                  >
                    <User className="h-4 w-4 text-white" />
                  </div>
                </button>

                {isUserMenuOpen && (
                  <div className="absolute right-0 z-[10001] mt-2 w-56 rounded-xl border border-m-border-light bg-m-surface py-1 shadow-[0_12px_40px_rgba(0,0,0,0.08)]">
                    <Link
                      to={profileHref}
                      onClick={() => setIsUserMenuOpen(false)}
                      className="block border-b border-m-border-light px-4 py-3 transition-colors hover:bg-m-canvas-light"
                    >
                      <div className="text-sm font-medium text-m-ink">{user?.name || 'User'}</div>
                      <div className="mt-1 truncate text-xs text-m-ink-muted">{user?.email}</div>
                    </Link>

                    {isAdmin ? (
                      <Link
                        to="/admin"
                        className={menuItemClass}
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <Shield className="mr-3 h-4 w-4" />
                        Go to Admin
                      </Link>
                    ) : isVendor ? (
                      <>
                        <Link
                          to="/vendor"
                          className={menuItemClass}
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <Calendar className="mr-3 h-4 w-4" />
                          Vendor Dashboard
                        </Link>
                        <Link
                          to="/vendor/profile"
                          className={menuItemClass}
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <Settings className="mr-3 h-4 w-4" />
                          Profile Settings
                        </Link>
                      </>
                    ) : (
                      <>
                        <Link
                          to="/dashboard"
                          className={menuItemClass}
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <Calendar className="mr-3 h-4 w-4" />
                          My Bookings
                        </Link>
                        <Link
                          to="/settings"
                          className={menuItemClass}
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <Settings className="mr-3 h-4 w-4" />
                          Profile Settings
                        </Link>
                      </>
                    )}
                    <button type="button" onClick={openLogoutModal} className={menuItemClass}>
                      <LogOut className="mr-3 h-4 w-4" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <Link
                  to="/login"
                  className={`px-3.5 py-2 text-[15px] font-normal tracking-tight transition-colors ${
                    shouldBeTransparent
                      ? 'text-white/85 hover:text-white'
                      : 'text-white/70 hover:text-white'
                  }`}
                >
                  Log in
                </Link>
                <Link
                  to="/register"
                  className="inline-flex h-9 items-center rounded-full bg-white px-4 text-[14px] font-medium tracking-tight text-m-ink transition-colors hover:bg-white/90"
                >
                  Start for free
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            type="button"
            onClick={() => setIsMenuOpen((open) => !open)}
            aria-expanded={isMenuOpen}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            className={`rounded-full p-2 transition-colors md:hidden ${
              shouldBeTransparent
                ? 'text-white drop-shadow-md hover:bg-white/10'
                : 'text-white/70 hover:bg-white/10 hover:text-white'
            }`}
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {mobileMenu}

      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => !loggingOut && setShowLogoutModal(false)}
        onConfirm={handleLogout}
        loading={loggingOut}
      />
    </header>
  );
};

export default ClientHeader;
